import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography
} from '@mui/material';
import { getDateToString, getDateTimeFromAPIDate } from 'src/utils/date';
import {
  CurrencyNumberFormatString,
  VolumeNumberFormatString
} from 'src/utils/numbers';
import { APARTMENT_OBJECT_TYPE, COLORS } from 'src/constants';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import "moment/locale/sv"; 
import {
  VolumeNumberFormatString as formatVolume,
  EnergyNumberFormatString as formatEnergy,
  CurrencyNumberFormatString as formatCurrency
} from 'src/utils/numbers';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ConsumptionTable({ title, consumption, volumeFormatter, fieldName }) {
  if (!consumption?.periods?.length) {
    return null;
  }

  const { periodSummary: summary } = consumption;

  const formatters = {
    volume: formatVolume,
    energy: formatEnergy,
    empty: () => ''
  };

  const formatPriceWithLabel = (priceSum) => {
    const absPrice = Math.abs(priceSum);
    const label = priceSum === 0 ? "" : priceSum < 0 ? 'Att betala' : 'Att få tillbaks';
    return (
      <>
        {formatCurrency(absPrice)}{" "}
        <Typography
          component="span"
          style={{
            fontSize: "0.8rem",
            marginLeft: 4,
            color: priceSum < 0 ? "red" : "green",
            fontWeight: "bold"
          }}
        >
          {label}
        </Typography>
      </>
    );
  };

  const typeMap = {
    coldwater: {
      text: 'Kallvatten',
      formatter: 'volume',
      visible: consumption.coldWaterSensorsExists
    },
    hotwater: {
      text: 'Varmvatten',
      formatter: 'volume',
      visible: consumption.hotWaterSensorsExists
    },
    electricity: {
      text: 'El',
      formatter: 'energy',
      visible: consumption.electicitySensorsExists
    },
    heatenergy: {
      text: 'Värmeenergi',
      formatter: 'energy',
      visible: consumption.heatEnergySensorsExists
    },
    evCharging: {
      text: 'Laddning av elbil',
      formatter: 'energy',
      visible: consumption.evChargingSensorsExists
    },
    kasEnergy: {
      text: 'Komfortavräkning',
      formatter: 'empty',
      visible: consumption.measurementObjectTypes.includes(
        APARTMENT_OBJECT_TYPE.KAS
      )
    }
  };

  const row = typeMap[fieldName];

  if (!row || !row.visible) {
    return null; // Visa inget om den inte finns eller är dold
  }

  const query = useQuery();
  const queryFromdate = query.get('from');
  const queryTodate = query.get('to');
  const queryResolution = Number(query.get('res'));

  const lastValidPeriod = [...consumption.periods]
    .reverse()
    .find(period => !period[fieldName]?.isMissingDay);

  let lastDateText = '';
  if (lastValidPeriod) {
    const dateObj = getDateTimeFromAPIDate(lastValidPeriod.readDate);
    lastDateText = queryResolution === 3
      ? moment(dateObj).locale('sv').format('MMMM YYYY')
      : getDateToString(dateObj);
    lastDateText = lastDateText.charAt(0).toUpperCase() + lastDateText.slice(1);
  }

  return (
    <>
    <TableContainer 
    sx={{mb: 2}}
    component={Paper}>
      <Typography variant="h6" style={{ padding: '16px' }}>
        {title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Datum</TableCell>
            <TableCell align="right">Förbrukning</TableCell>
            <TableCell align="right">Kostnad</TableCell>
            <TableCell>Typ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {consumption.periods.map((period, idx) => {
            const entry = period[fieldName];
            //const date = getDateToString(getDateTimeFromAPIDate(period.readDate));

            const dateObj = getDateTimeFromAPIDate(period.readDate);

            // Standarddatum
            let date = getDateToString(dateObj);

            // Om resolution är 3 => visa "augusti 2025"
            if (queryResolution === 3) {
              date = moment(dateObj).locale("sv").format("MMMM YYYY"); 
              date = date.charAt(0).toUpperCase() + date.slice(1);
            }

            const volume = volumeFormatter(entry.value);
            const priceSum = entry.priceSum;
            const absPrice = CurrencyNumberFormatString(Math.abs(priceSum));
            const typeLabel = entry.isMissingDay ? "Tom data" : priceSum === 0 ? "" : priceSum < 0 ? "Att betala" : "Att få tillbaks";

            return (
              <TableRow key={idx}>
                <TableCell>{date}</TableCell>
                <TableCell align="right">{volume}</TableCell>
                <TableCell align="right" sx={{color: entry.isMissingDay ? "" : priceSum < 0 ? "red" : "green"}}>{absPrice}</TableCell>
                <TableCell>{typeLabel}</TableCell>
              </TableRow>
            );
          })}
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{fontWeight: "bold"}}>Summering:</TableCell>
            <TableCell align="right">
              {formatters[row.formatter](summary[fieldName].value)}
            </TableCell>
            <TableCell align="right" sx={{color: summary[fieldName].priceSum < 0 ? "red" : "green"}}>
              {CurrencyNumberFormatString(Math.abs(summary[fieldName].priceSum))}
            </TableCell>
            <TableCell>
              {summary[fieldName].priceSum === 0 ? "" : summary[fieldName].priceSum < 0 ? "Att betala" : "Att få tillbaks"}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
    {(lastDateText && consumption.periods.some(period => period[fieldName]?.isMissingDay === true)) && (
      <Typography variant="body2" sx={{mt: 2, mb: 2}} color="">
        Det finns inga värden att visa för datum senare än {lastDateText}
      </Typography>
    )}
    </>
  );
}

export default ConsumptionTable;
